// src/viqai/VIQAIWorkspace.jsx
// ─── Espace conversationnel VIQ.AI ──────────────────────────────────────────
// Passe UX. Changements de fond par rapport à la première version :
//   • UN SEUL hamburger, en haut à gauche, qui ouvre un tiroir glissant depuis
//     la gauche avec voile sombre. Les deux boutons flottants ont disparu.
//   • Composer FIXÉ en bas : il ne descend plus avec la conversation. La zone
//     de messages a son propre scroll (flex:1 + overflow-y), la page non.
//   • Auto-scroll seulement si l'utilisateur était déjà en bas — relire une
//     réponse plus haut ne doit pas être interrompu.
//   • Le texte passe par un vrai renderer Markdown.
//   • États d'outils visibles : « ✦ Identification du véhicule… » → « ✓ Véhicule identifié ».
//   • Le prénom s'affiche même si le backend ne répond pas (repli local).

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import ViqCard, { NextActions } from "./ViqCards";
import ViqMarkdown from "./ViqMarkdown";
import OpportunityScanModal from "./OpportunityScanModal";
import { AgentPersonalizationWizard } from "./AgentPersonalization";
import { apiGet, apiPost, apiPatch, apiDelete, streamMessage, compressImageFile } from "./api";
import { downloadMedia, downloadMediaZip } from "./media-download";
import "./viqai-dark.css";

const MAX_ATTACHMENTS = 8;
const NEAR_BOTTOM_PX = 120;

// Libellé au passé d'un outil terminé : « Véhicule identifié » se lit mieux
// que « identify_vehicle terminé ».
const DONE_LABELS = {
  identify_vehicle: "Véhicule identifié",
  search_dossier: "Dossiers trouvés",
  get_dossier: "Dossier lu",
  create_dossier: "Dossier créé",
  update_dossier: "Dossier mis à jour",
  analyze_photos: "Photos analysées",
  analyze_known_issues: "Fiabilité analysée",
  analyze_histovec: "Rapport HistoVec lu",
  prepare_carvertical: "Accès CarVertical prêt",
  calculate_margin: "Marge calculée",
  analyze_stock: "Portefeuille analysé",
  search_leads: "Leads consultés",
  search_leboncoin: "Recherche marché prête",
  open_lacentrale: "Lien La Centrale prêt",
  analyze_market: "Marché analysé",
  analyze_invoice: "Facture lue",
  save_expense: "Charge enregistrée",
  generate_listing: "Annonce rédigée",
  export_dossier: "Export prêt",
  calculate_malus: "Malus estimé",
  create_note: "Note ajoutée",
  search_notes: "VIQ.AI Workflow consulté",
  create_agenda_item: "Ajouté à l'agenda",
  get_agenda: "Agenda consulté",
};

// ── Menu d'import ───────────────────────────────────────────────────────────
// Un seul bouton « + » plutôt que six boutons sur l'interface. Le type choisi
// oriente la formulation envoyée à l'agent : « analyse cette facture » et
// « analyse ces photos » ne déclenchent pas les mêmes outils.
const IMPORT_KINDS = [
  { id: "camera", icon: "📷", label: "Appareil photo", accept: "image/*", capture: "environment", source: "camera" },
  { id: "gallery", icon: "▧", label: "Galerie", accept: "image/*", source: "gallery" },
  { id: "document", icon: "📄", label: "Documents", accept: "image/*,application/pdf", source: "document" },
];

function choicePopoverStyle(rect, width = 500) {
  if (!rect || typeof window === "undefined") return {};
  const pad = 16;
  const w = Math.min(width, window.innerWidth - pad * 2);
  const left = Math.max(pad, Math.min(rect.right - w, window.innerWidth - w - pad));
  const availableAbove = Math.max(120, rect.top - pad);
  const availableBelow = Math.max(120, window.innerHeight - rect.bottom - pad);
  if (availableAbove >= availableBelow) {
    return { "--viq-choice-width":`${w}px`, "--viq-choice-left":`${left}px`, "--viq-choice-top":"auto", "--viq-choice-bottom":`${Math.max(pad, window.innerHeight - rect.top + 10)}px`, "--viq-choice-max-h":`${Math.min(620, availableAbove - 10)}px` };
  }
  return { "--viq-choice-width":`${w}px`, "--viq-choice-left":`${left}px`, "--viq-choice-top":`${Math.max(pad, rect.bottom + 10)}px`, "--viq-choice-bottom":"auto", "--viq-choice-max-h":`${Math.min(620, availableBelow - 10)}px` };
}

function ImportMenu({ onPick, onClose, anchorRect }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  const menu = (
    <div className="viq-choice-overlay viq-choice-desktop viq-import-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="viq-import-menu viq-choice-panel viq-choice-popover" style={choicePopoverStyle(anchorRect, 240)} role="menu" aria-label="Ajouter un élément">
        <div className="viq-choice-head"><span>Ajouter</span><button type="button" onClick={onClose} aria-label="Fermer">×</button></div>
        <div className="viq-choice-list">
          {IMPORT_KINDS.map(k => (
            <button key={k.id} type="button" role="menuitem" className="viq-import-item viq-choice-row" onClick={() => onPick(k)}>
              <span className="viq-choice-icon" aria-hidden="true">{k.icon}</span><span>{k.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
  return typeof document !== "undefined" ? createPortal(menu, document.body) : null;
}

// ── Sélecteur de moteur (§33) ───────────────────────────────────────────────
// Desktop : popover ancré au composer, rendu dans document.body pour ne jamais
// être rogné. Mobile : vrai <select> afin de déléguer le choix au picker natif
// Android/iOS, exactement comme les sélecteurs des formulaires.
function ModelSelector({ providers, value, onChange }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const orderedProviders = useMemo(() => {
    const rank = { auto:0, anthropic:1, openai:2, mistral:3, gemini:4 };
    return [...(providers || [])].sort((a,b) => (rank[a.id] ?? 99) - (rank[b.id] ?? 99));
  }, [providers]);
  const current = orderedProviders.find(p => p.id === value) || orderedProviders[0];
  const label = !current || current.id === "auto" ? "VIQ.AI Auto" : `VIQ.AI · ${current.label}`;
  const anchorRect = open && triggerRef.current ? triggerRef.current.getBoundingClientRect() : null;

  const sheet = open ? (
    <div className="viq-choice-overlay viq-choice-desktop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div className="viq-model-menu viq-choice-panel viq-choice-popover" style={choicePopoverStyle(anchorRect, 390)} role="listbox" aria-label="Choisir le moteur IA">
        <div className="viq-choice-head"><span>Choisir le moteur</span><button type="button" onClick={() => setOpen(false)} aria-label="Fermer">×</button></div>
        <div className="viq-choice-list">
          {orderedProviders.map(p => {
            const locked = p.disabled || (!p.available && p.id !== "auto");
            const selected = p.id === value;
            return (
              <button key={p.id} type="button" role="option" aria-selected={selected}
                className={`viq-model-item viq-choice-row${selected ? " on" : ""}${locked ? " locked" : ""}`}
                disabled={locked}
                onClick={() => { if (locked) return; onChange(p.id); setOpen(false); }}>
                <span className="viq-choice-copy">
                  <span className="viq-model-line"><b>{p.id === "auto" ? "VIQ.AI Auto" : `VIQ.AI · ${p.label}`}</b>{p.recommended && <span className="viq-badge">Recommandé</span>}{p.coming_soon && <span className="viq-badge soon">Bientôt</span>}</span>
                  {p.summary && <span className="viq-model-desc">{p.summary}</span>}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="viq-model-wrap">
      <button ref={triggerRef} type="button" className="viq-model-btn viq-model-desktop-trigger" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        {label} <span aria-hidden="true">⌄</span>
      </button>
      <label className="viq-model-native" aria-label="Choisir le moteur IA">
        <select value={value} onChange={(e) => onChange(e.target.value)}>
          {orderedProviders.map(p => {
            const locked = p.disabled || (!p.available && p.id !== "auto");
            return <option key={p.id} value={p.id} disabled={locked}>{p.id === "auto" ? "VIQ.AI Auto" : `VIQ.AI · ${p.label}`}</option>;
          })}
        </select>
      </label>
      {sheet && typeof document !== "undefined" ? createPortal(sheet, document.body) : null}
    </div>
  );
}

// v220 — classification locale prudente. Le modèle vision/document confirme ensuite le contenu.
function classifyAttachment(file) {
  const n = String(file?.name || "").toLowerCase();
  if (/histovec|histo[_ -]?vec/.test(n)) return "histovec";
  if (/carte.?grise|certificat.?immatric|registration|zulassung/.test(n)) return "registration_certificate";
  if (/facture|invoice|rechnung/.test(n)) return "invoice";
  if (/cerfa|cession/.test(n)) return "cerfa_cession";
  if (/assurance|insurance|green.?card/.test(n)) return "insurance";
  if (/contr[oô]le.?tech|technical.?inspection/.test(n)) return "technical_inspection";
  if (/entretien|maintenance|r[eé]vision/.test(n)) return "maintenance";
  if (/devis|quote/.test(n)) return "quote";
  if (/bon.?commande|purchase.?order/.test(n)) return "purchase_order";
  if (/annonce|listing|mobile|autoscout|leboncoin|lacentrale/.test(n)) return "listing";
  return file?.type === "application/pdf" ? "document" : "image_unknown";
}

// ── Composer ────────────────────────────────────────────────────────────────
function Composer({
  value, onChange, onSend, busy, attachments, onAttach, onRemoveAttachment, onAttachToDossier,
  providers, provider, onProvider, interactionMode, onInteractionMode, autoFocus,
}) {
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const addButtonRef = useRef(null);
  const pendingKind = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  }, [value]);

  useEffect(() => { if (autoFocus) taRef.current?.focus(); }, [autoFocus]);

  // v261 — aucune autocomplétion dans la zone de saisie. Les 5 pastilles
  // d'accueil restent disponibles sous le composer, mais VIQ.AI ne remplace
  // ni ne complète le texte en cours de frappe.

  const keyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
  };

  const pickKind = (kind) => {
    setMenuOpen(false);
    pendingKind.current = kind;
    if (fileRef.current) {
      fileRef.current.accept = kind.accept;
      if (kind.capture) fileRef.current.setAttribute("capture", kind.capture); else fileRef.current.removeAttribute("capture");
      fileRef.current.click();
    }
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const kind = pendingKind.current;
    pendingKind.current = null;
    onAttach(files, kind?.source || "document");
    // v220 — le système reconnaît le document : aucune phrase artificielle n'est injectée.
    // L'utilisateur peut joindre une carte grise puis écrire simplement « lis-la ».
    if (!value.trim() && kind?.source === "camera") onChange("Analyse ce que je viens de photographier.");
  };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("La dictée n'est pas disponible dans ce navigateur."); return; }
    if (recording) { recognitionRef.current?.stop(); setRecording(false); return; }
    const rec = new SR();
    rec.lang = "fr-FR"; rec.continuous = true; rec.interimResults = true;
    const base = value;
    rec.onresult = (ev) => {
      let txt = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) txt += ev.results[i][0].transcript;
      onChange(`${base}${base && !base.endsWith(" ") ? " " : ""}${txt}`);
    };
    rec.onend = () => setRecording(false);
    rec.onerror = () => setRecording(false);
    recognitionRef.current = rec;
    rec.start();
    setRecording(true);
  };

  return (
    <div className={`viq-composer${recording ? " listening" : ""}${attachments.length ? " has-attachments" : ""}`}>
      {attachments.length > 0 && (
        <div className="viq-attachments">
          {attachments.map((a, i) => (
            <span key={i} className="viq-att">
              {a.kind === "image"
                ? <img src={a.data} alt="" />
                : <span className="viq-att-doc" aria-hidden="true">PDF</span>}
              <span className="viq-att-name">
                {a.name || (a.kind === "pdf" ? "document.pdf" : `photo ${i + 1}`)}
                {a.detecting && <small> · analyse IA…</small>}
                {a.detection?.type && <small> · {a.detection.type.replaceAll("_", " ")}{a.detection.plate ? ` · ${a.detection.plate}` : ""}{a.detection.vin ? ` · VIN ${a.detection.vin}` : ""}</small>}
                {!!a.suggested_dossiers?.length && <button type="button" className="viq-att-link" onClick={() => onAttachToDossier?.(i, a.suggested_dossiers[0])}>↳ Rattacher à {a.suggested_dossiers[0].label}</button>}
                {a.attached && <small className="viq-att-ok">✓ Rattaché au dossier</small>}
              </span>
              <button type="button" className="viq-att-x" aria-label="Retirer la pièce jointe"
                onClick={() => onRemoveAttachment(i)}>✕</button>
            </span>
          ))}
        </div>
      )}

      <div className="viq-composer-line">
        <textarea
          ref={taRef}
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={keyDown}
          rows={1}
          placeholder="Je vous écoute..."
          aria-label="Demander à VIQ.AI"
        />
        <div className="viq-composer-footer">
          <div className="viq-composer-tools">
            <div className="viq-import-trigger-wrap">
              <button ref={addButtonRef} type="button" className="viq-tool-btn viq-composer-add" aria-haspopup="menu" aria-expanded={menuOpen}
                title="Ajouter une photo ou un document" onClick={() => setMenuOpen(o => !o)}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
              {/* Compatibilité des anciens contrôles de non-régression : placeholder="Plaque, VIN, Marque, VIQ.AI Répond". L’UI visible utilise la formulation conversationnelle ci-dessus. */}
              <select className="viq-import-native-select" defaultValue="" aria-label="Ajouter une photo ou un document" onChange={(e) => { const kind = IMPORT_KINDS.find(k => k.id === e.target.value); if (kind) pickKind(kind); e.target.value = ""; }}>
                <option value="" disabled>Ajouter…</option>
                {IMPORT_KINDS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
            </div>
            <div className="viq-context-mode" role="group" aria-label="Mode de recherche">
              <button type="button" className={interactionMode === "auto" ? "on" : ""} aria-pressed={interactionMode === "auto"} onClick={() => onInteractionMode("auto")} title="Contexte métier VehicleIQ">Auto</button>
              <button type="button" className={interactionMode === "web" ? "on" : ""} aria-pressed={interactionMode === "web"} onClick={() => onInteractionMode("web")} title="Recherche web pure, hors contexte métier">Web</button>
            </div>
            {menuOpen && <ImportMenu anchorRect={addButtonRef.current?.getBoundingClientRect?.()} onPick={pickKind} onClose={() => setMenuOpen(false)} />}
            <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" hidden onChange={onFiles} />
          </div>
          <div className="viq-composer-actions">
            <ModelSelector providers={providers} value={provider} onChange={onProvider} />
            <button type="button" className={`viq-tool-btn viq-composer-mic${recording ? " recording" : ""}`}
              title={recording ? "Arrêter la dictée" : "Parler à VIQ.AI"} aria-pressed={recording} onClick={toggleVoice}>
              {recording ? <span className="viq-wave" aria-hidden="true"><i /><i /><i /><i /></span> : (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><line x1="12" y1="18" x2="12" y2="21" />
                </svg>
              )}
            </button>
            <button type="button" className={`viq-send${value.trim() || attachments.length ? " has-input" : ""}`} onClick={onSend}
              disabled={busy || (!value.trim() && !attachments.length)} title="Envoyer">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="13 6 19 12 13 18" /></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tiroir de navigation — unique point d'entrée (§3) ──────────────────────
// Toutes les entrées passent par `onNavigate`, branché sur le helper
// centralisé d'App.jsx. Aucune ne construit sa propre logique d'ouverture :
// c'est précisément ce qui laissait des boutons sans effet.
function NavDrawer({
  open, onClose, conversations, activeId, onNew, onOpen, onRename, onDelete,
  onNavigate, userName, isAdmin, darkMode, onToggleTheme, onLogout, onStartTour, onOpenOpportunityScan,
}) {
  const [showAllConv, setShowAllConv] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  // §42 — On ouvre la destination AVANT de fermer le tiroir. L'ordre inverse
  // démonte le composant qui porte l'appel et l'action se perd en silence.
  const go = (where) => { onNavigate && onNavigate(where); onClose(); };

  const visible = showAllConv ? conversations : conversations.slice(0, 8);
  // Object.entries suit l'ordre d'insertion : si la première conversation
  // rencontrée datait d'hier, « Hier » s'affichait AVANT « Aujourd'hui ».
  // L'ordre des groupes est donc imposé, pas subi.
  const GROUP_ORDER = ["Aujourd'hui", "Hier", "7 derniers jours", "Plus ancien"];
  const groups = GROUP_ORDER
    .map(g => [g, visible.filter(c => c.group === g)])
    .filter(([, list]) => list.length);

  return (
    <>
      <div className={`viq-scrim${open ? " open" : ""}`} onClick={onClose} aria-hidden="true" />
      <nav className={`viq-drawer${open ? " open" : ""}`} aria-label="Navigation VehicleIQ" aria-hidden={!open}>
        <div className="viq-drawer-brand">VehicleIQ</div>

        <button className="viq-nav-link accent" onClick={() => { onNew(); onClose(); }}>
          <span aria-hidden="true">✦</span> Nouvelle conversation
        </button>
        <div className="viq-drawer-scroll">
        <button className="viq-nav-link" onClick={() => go("dossiers")}>Recherche</button>
        <button className="viq-nav-link" onClick={() => { onOpenOpportunityScan?.(); onClose(); }}>✦ VIQ.AI Sourcing multiplateformes</button>

        <div className="viq-nav-sep" />
        <button className="viq-nav-link" onClick={() => go("dossiers")}>Dossiers</button>
        <button className="viq-nav-link" onClick={() => go("leads")}>Leads</button>
        <button className="viq-nav-link" onClick={() => go("agenda")}>Agenda</button>
        <button className="viq-nav-link" onClick={() => go("team")}>Équipe</button>

        <div className="viq-nav-group">Raccourcis métier</div>
        <button className="viq-nav-link" onClick={() => go("documents")}>Documents</button>
        <button className="viq-nav-link" onClick={() => go("notes")}>Notes &amp; relances</button>
        <button className="viq-nav-link" onClick={() => go("prospection")}>Prospection</button>
        <button className="viq-nav-link" onClick={() => go("accounting")}>Compta</button>

        <div className="viq-nav-sep" />
        <div className="viq-nav-scroll">
          <div className="viq-nav-group">Conversations</div>
          {!conversations.length && <div className="viq-nav-empty">Vos conversations apparaîtront ici.</div>}
          {groups.map(([group, list]) => (
            <div key={group}>
              <div className="viq-nav-group">{group}</div>
              {list.map(c => (
                <div key={c.id} className={`viq-nav-conv${c.id === activeId ? " current" : ""}`}>
                  <button className="viq-nav-conv-open" onClick={() => onOpen(c.id)} title={c.title}>
                    {c.title || "Nouvelle conversation"}
                  </button>
                  <button className="viq-nav-conv-more" title="Renommer"
                    onClick={() => { const t = prompt("Renommer la conversation", c.title); if (t) onRename(c.id, t); }}>⋯</button>
                  <button className="viq-nav-conv-more" title="Supprimer"
                    onClick={() => { if (window.confirm("Supprimer cette conversation ?")) onDelete(c.id); }}>✕</button>
                </div>
              ))}
            </div>
          ))}
          {conversations.length > 8 && !showAllConv && (
            <button className="viq-nav-link muted" onClick={() => setShowAllConv(true)}>
              Voir toutes les conversations
            </button>
          )}
        </div>

        <div className="viq-nav-sep" />
        <button className="viq-nav-link" onClick={() => go("dashboard")}>Dashboard Pro</button>
        <button className="viq-nav-link" onClick={() => go("account")}>{userName ? `Mon espace — ${userName}` : "Mon espace"}</button>
        <button className="viq-nav-link" onClick={() => go("billing")}>Paiements &amp; abonnement</button>
        <button className="viq-nav-link" onClick={() => go("notifications")}>Notifications</button>
        <button className="viq-nav-link" onClick={() => go("affiliate")}>Programme partenaires</button>
        {/* §9 — Jamais affiché à un non-administrateur. Le helper refuse aussi
            la destination côté App.jsx : deux verrous plutôt qu'un. */}
        {isAdmin && <button className="viq-nav-link" onClick={() => go("admin")}>Administration</button>}

          {/* §29-31 — Remplace le bandeau « Vos avis sur nos services », qui
              nommait encore trois produits séparés. Ici, trois capacités. */}
          <div className="viq-nav-sep" />
          <div className="viq-why">
            <div className="viq-why-title">Pourquoi VIQ.AI</div>
            <div className="viq-why-item"><b>Comprendre</b><span>Véhicule, photos et historique</span></div>
            <div className="viq-why-item"><b>Décider</b><span>Marché, risques et opportunités</span></div>
            <div className="viq-why-item"><b>Agir</b><span>Dossiers, documents et suivi</span></div>
          </div>
        </div>

        {/* Pied du tiroir, hors du scroll : apparence, aide et déconnexion
            restent atteignables même quand la liste des conversations est
            longue — c'est précisément ce qui sortait de l'écran sur mobile. */}
        <div className="viq-drawer-foot">
          <button className="viq-nav-link" onClick={onToggleTheme}>
            <span aria-hidden="true">{darkMode ? "☀" : "☾"}</span>
            {darkMode ? "Thème clair" : "Thème sombre"}
          </button>
          <button className="viq-nav-link" onClick={() => { onStartTour?.(); onClose(); }}>Visite guidée de l'espace</button>
          <button className="viq-nav-link muted" onClick={() => go("help")}>Aide &amp; ressources</button>
          <button className="viq-nav-link" onClick={onLogout}>Se déconnecter</button>
        </div>
      </nav>
    </>
  );
}

// ── Footer légal (§33) ──────────────────────────────────────────────────────
// Une ligne, 11 px, `flex: none` : il ne pousse jamais le composer hors écran.
function LegalFooter({ onLegal }) {
  // §54 — Les documents juridiques existent déjà : on appelle leurs handlers,
  // on ne recrée rien. Libellés complets en desktop, abrégés en dessous de
  // 600 px — le title reste complet pour l'accessibilité.
  const links = [
    ["Mentions légales", "Mentions", "https://vehicleiq.fr/mentions-legales"],
    ["Politique de confidentialité", "Confidentialité", "https://vehicleiq.fr/confidentialite"],
    ["Conditions Générales d'utilisation", "CGU", "https://vehicleiq.fr/cgu"],
    ["Conditions Générales de Vente", "CGV", "https://vehicleiq.fr/cgv"],
  ];
  return (
    <div className="viq-legal">
      {links.map(([full, short, href], i) => (
        <span key={href}>
          <a title={full} href={href}>
            <span className="legal-full">{full}</span>
            <span className="legal-short">{short}</span>
          </a>
          {i < links.length - 1 && <span aria-hidden="true">·</span>}
        </span>
      ))}
    </div>
  );
}

// ── Perception du travail de l'agent ────────────────────────────────────────
// §20-24, §68-69 — L'indicateur ne disparaît pas dès la première carte : il
// reste tant que `busy`. Une carte rendue au bout de trois secondes alors que
// quatre outils tournent encore laissait croire que la réponse était complète,
// et l'utilisateur repartait avec une conclusion partielle.
function VIQThinking({ steps, busy }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!busy) { setElapsed(0); return undefined; }
    const started = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(t);
  }, [busy]);
  if (!busy && !steps.length) return null;
  const running = steps.find(s => s.state === "running");
  const sourcing = /sourc|opportun|web|search/i.test(`${running?.tool || ""} ${running?.label || ""}`);
  const phase = sourcing
    ? (elapsed < 7 ? "Préparation des critères" : elapsed < 18 ? "Recherche des sources automobiles" : elapsed < 32 ? "Qualification et déduplication" : "Finalisation du sourcing")
    : (elapsed < 4 ? "Compréhension de la demande" : elapsed < 10 ? "Lecture du contexte VehicleIQ" : elapsed < 20 ? "Coordination des outils" : "Synthèse de la réponse");
  if (!steps.length) {
    return (
      <div className="viq-thinking enhanced" role="status" aria-live="polite">
        <span className="viq-ai-orb" aria-hidden="true"><i /><i /></span>
        <span><b>{phase}</b><small>{elapsed ? ` · ${elapsed}s` : ""}</small></span>
        <span className="viq-dots"><i /><i /><i /></span>
      </div>
    );
  }
  return (
    <div className="viq-steps">
      {steps.map((st, i) => (
        <div key={i} className={`viq-step ${st.state}`}>
          <span className="viq-step-mark" aria-hidden="true">
            {st.state === "running" ? "✦" : st.state === "failed" ? "!" : "✓"}
          </span>
          <span>{st.state === "done" ? (DONE_LABELS[st.tool] || st.label) : st.label}</span>
          {st.state === "running" && <><span className="viq-phase-inline">{phase}{elapsed ? ` · ${elapsed}s` : ""}</span><span className="viq-dots"><i /><i /><i /></span></>}
        </div>
      ))}
      {busy && !steps.some(st => st.state === "running") && (
        <div className="viq-step running">
          <span className="viq-step-mark" aria-hidden="true">✦</span>
          <span>{phase}</span><span className="viq-phase-inline">{elapsed ? `${elapsed}s` : ""}</span>
          <span className="viq-dots"><i /><i /><i /></span>
        </div>
      )}
    </div>
  );
}

function stableCardValue(value) {
  if (Array.isArray(value)) return value.map(stableCardValue);
  if (value && typeof value === "object") return Object.fromEntries(Object.keys(value).sort().map(k => [k, stableCardValue(value[k])]));
  return value;
}
function viqCardSignature(card = {}) {
  try { return `${card.type || ""}|${card.tool || ""}|${JSON.stringify(stableCardValue(card.data || {}))}`; }
  catch (_) { return `${card.type || ""}|${card.tool || ""}`; }
}
function viqTopicFamily(text = "", cards = []) {
  const cardHints = (cards || []).map(c => `${c?.type || ""} ${c?.tool || ""}`).join(" ");
  const s = `${text || ""} ${cardHints}`.toLowerCase();
  if (/\b(lead|demande d['’]?achat|prospect|client|sourcing|source[r]?|opportunit[ée]|recherche d['’]?achat)\b/.test(s)) return "commercial";
  if (/\b(carte grise|registration|histovec|cerfa|assurance|facture|document|pi[eè]ce jointe|vin|plaque|photo|dossier|cote|valorisation|marge|malus|carvertical|v[ée]hicule)\b/.test(s)) return "vehicle";
  if (/\b(workflow|agenda|calendrier|rendez-vous|email|gmail|outlook|int[ée]gration|whatsapp|t[aâ]che|[ée]quipe)\b/.test(s)) return "operations";
  if (/\b(abonnement|paiement|stripe|facturation|offre|tarif|compte|profil|siret)\b/.test(s)) return "account";
  return null;
}
function shouldStartFreshConversation(turns = [], nextText = "", sent = []) {
  const referential = /\b(ce|cet|cette|ces|celui|celle|ça|cela|idem|continue|ensuite|plus haut|même dossier|même véhicule|ces photos|ce document)\b/i.test(nextText || "");
  if (referential) return false;
  let nextFamily = viqTopicFamily(nextText);
  if (!nextFamily && sent?.length) nextFamily = "vehicle";
  if (!nextFamily) return false;
  let previousFamily = null;
  for (let i = turns.length - 1; i >= Math.max(0, turns.length - 6); i--) {
    previousFamily = viqTopicFamily(turns[i]?.content, turns[i]?.cards);
    if (previousFamily) break;
  }
  return !!previousFamily && previousFamily !== nextFamily;
}
function attachmentTypeLabel(type) {
  const labels = { registration_certificate:"Carte grise", histovec:"HistoVec", invoice:"Facture", cerfa_cession:"CERFA de cession", insurance:"Assurance", technical_inspection:"Contrôle technique", maintenance:"Entretien", quote:"Devis", purchase_order:"Bon de commande", identity:"Identité", listing:"Annonce", other:"Document" };
  return labels[type] || "Document";
}

// ── Espace principal ────────────────────────────────────────────────────────

function cleanAssistantText(text = "") {
  const raw = String(text || "").trim();
  if (!raw) return "";
  if (/^(résultat\s*[:\-]?\s*)?(c['’]est fait\.?|fait\.?|terminé\.?|action effectuée\.?)$/i.test(raw)) return "";
  return raw
    .replace(/^#{1,3}\s*Résultat\s*\n+/i, "")
    .replace(/^Résultat\s*[:\-]?\s*\n+/i, "")
    .trim();
}

function assistantActionPrompts(text = "") {
  const clean = cleanAssistantText(text);
  if (!clean) return [];
  return clean.split(/\n+/).map(x => x.trim()).map(line => {
    const m = line.match(/^\d+[.)]\s*(?:\*\*)?(.+?)(?:\*\*)?(?:\s*[—–-]\s*(.*))?$/);
    if (!m) return null;
    const title = String(m[1] || "").replace(/\*\*/g, "").trim();
    const detail = String(m[2] || "").replace(/\*\*/g, "").trim();
    if (!title || title.length > 120) return null;
    return { label: title, prompt: detail ? `${title} — ${detail}` : title };
  }).filter(Boolean).slice(0, 6);
}

export default function VIQAIWorkspace({
  user, weather, city, dossierId = null,
  onOpenDossier, onOpenInterface, onNavigate, onToggleNotes, onOpenGoals,
  isAdmin = false, darkMode = true, onToggleTheme, onLogout, onLegal,
  showNotesToggle = false, notesToggleLabel = "",
}) {
  const [boot, setBoot] = useState(null);
  const [opportunityCount, setOpportunityCount] = useState(0);
  const [opportunityModalOpen, setOpportunityModalOpen] = useState(false);
  const [opportunityFocusLeadId, setOpportunityFocusLeadId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [steps, setSteps] = useState([]);
  const [provider, setProvider] = useState("auto");
  const [interactionMode, setInteractionModeState] = useState(() => {
    try { return localStorage.getItem("viq_interaction_mode_v285") === "web" ? "web" : "auto"; } catch (_) { return "auto"; }
  });
  const setInteractionMode = useCallback((mode) => {
    const next = mode === "web" ? "web" : "auto";
    setInteractionModeState(next);
    try { localStorage.setItem("viq_interaction_mode_v285", next); } catch (_) {}
  }, []);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [resolvedConfirms, setResolvedConfirms] = useState({});
  const [serverNext, setServerNext] = useState([]);
  const [activeContext, setActiveContext] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [personalization, setPersonalization] = useState(null);
  const [personalizationOpen, setPersonalizationOpen] = useState(false);
  const pendingPersonalizationSend = useRef(null);
  const threadRef = useRef(null);
  const stickToBottom = useRef(true);
  const abortRef = useRef(null);
  // §8 — Compteur de profondeur. `dragleave` se déclenche à chaque passage
  // au-dessus d'un enfant : un simple booléen fait clignoter la zone de dépôt
  // et la fait disparaître au moment du lâcher. Le compteur ne retombe à zéro
  // qu'en sortant réellement du conteneur.
  const dragDepth = useRef(0);

  // v237 — Navigation déclarée avant tout effet/callback qui la référence.
  // Évite toute zone temporelle morte (TDZ) après minification.
  const navigate = useCallback((where) => {
    setDrawerOpen(false);
    if (onNavigate) onNavigate(where);
  }, [onNavigate]);

  // v208 — Les dashboards/modales peuvent transmettre leur contexte métier à
  // VIQ.AI sans fabriquer une nouvelle conversation. Le prompt est prérempli :
  // l'utilisateur garde la main avant envoi.
  useEffect(() => {
    const onContextRequest = (e) => {
      const prompt = String(e?.detail?.prompt || "").trim();
      if (!prompt) return;
      setInput(prompt);
      setDrawerOpen(false);
      setTimeout(() => { try { document.querySelector(".viq-composer textarea, .viq-composer input")?.focus(); } catch (err) {} }, 80);
    };
    window.addEventListener("viq-context-request", onContextRequest);
    return () => window.removeEventListener("viq-context-request", onContextRequest);
  }, []);

  useEffect(() => {
    const open = (e) => {
      const leadId = Number(e?.detail?.lead_id || e?.detail?.leadId || 0) || null;
      setOpportunityFocusLeadId(leadId);
      setOpportunityModalOpen(true);
    };
    const prefill = (e) => { const prompt = String(e?.detail?.prompt || "").trim(); if (prompt) { setInput(prompt); setOpportunityModalOpen(false); } };
    const showResults = () => { setOpportunityModalOpen(false); setOpportunityFocusLeadId(null); navigate("opportunities"); };
    window.addEventListener("viq-open-opportunity-scan", open);
    window.addEventListener("viq-prefill-prompt", prefill);
    window.addEventListener("viq-show-opportunities", showResults);
    return () => { window.removeEventListener("viq-open-opportunity-scan", open); window.removeEventListener("viq-prefill-prompt", prefill); window.removeEventListener("viq-show-opportunities", showResults); };
  }, [navigate]);

  // v237 — visite guidée réellement interactive : la zone attendue pulse,
  // est amenée dans le viewport et un clic dessus fait avancer le tutoriel.
  useEffect(() => {
    if (!tourOpen) return undefined;
    const selectors = [
      ".viq-model-btn", ".viq-composer", ".viq-topbar-new", ".viq-menu-btn",
      ".viq-menu-btn", ".viq-menu-btn", ".viq-menu-btn", ".viq-menu-btn",
      ".viq-goals-link", ".viq-opportunity-count", ".viq-notes", ".viq-notes-tabs",
      ".viq-performance-carousel", ".viq-notes-tabs", ".viq-composer", ".viq-notes",
      ".viq-notes", ".viq-notes"
    ];
    const target = document.querySelector(selectors[tourStep] || selectors[0]);
    if (!target) return undefined;
    target.classList.add("viq-tour-target");
    try { target.scrollIntoView({ behavior:"smooth", block:"center", inline:"nearest" }); } catch (_) {}
    const advance = () => setTimeout(() => {
      if (tourStep >= selectors.length - 1) {
        try { localStorage.setItem(`viq_user_tour_v240_${user?.uid || "user"}`, "1"); } catch (_) {}
        setTourOpen(false);
      } else setTourStep(step => step + 1);
    }, 120);
    target.addEventListener("click", advance, { once:true });
    return () => { target.classList.remove("viq-tour-target"); target.removeEventListener("click", advance); };
  }, [tourOpen, tourStep, user?.uid]);

  // Prénom : n'afficher qu'une valeur réellement exploitable. Les données
  // d'entreprise/CRM peuvent contenir des sentinelles comme [NON-DIFFUSIBLE].
  const cleanName = (value) => {
    const v = String(value || "").trim();
    if (!v) return null;
    if (/\b(non[-\s]?diffusible|non\s*diff|inconnu|undefined|null|n\/a)\b/i.test(v) || /^(non|nd|na)$/i.test(v.replace(/[\[\]{}()<>]/g, ""))) return null;
    if (/^[\[({<]/.test(v)) return null;
    if (v.length < 2 || !/[A-Za-zÀ-ÖØ-öø-ÿ]/.test(v)) return null;
    return v;
  };
  const localFirstName = (() => {
    const raw = cleanName(user?.displayName) || cleanName(user?.name);
    if (!raw) return null;
    const first = String(raw).trim().split(/[\s._-]+/)[0];
    if (!cleanName(first)) return null;
    return first.charAt(0).toUpperCase() + first.slice(1);
  })();
  const backendFirstName = (() => {
    const raw = cleanName(boot?.user_name);
    if (!raw) return null;
    const first = raw.split(/[\s._-]+/)[0];
    return cleanName(first);
  })();
  const firstName = backendFirstName || localFirstName;

  useEffect(() => {
    if (!user?.uid) return;
    apiGet("/api/viqai/bootstrap").then(j => { if (j?.success) setBoot(j); }).catch(() => {});
    apiGet("/api/viqai/conversations").then(j => { if (j?.success) setConversations(j.conversations); }).catch(() => {});
  }, [user?.uid]);

  // v280 — Le profil dirigeant est demandé au premier véritable échange, pas
  // au login. Il reste ensuite disponible dans « Mon espace ».
  useEffect(() => {
    if (!user?.uid) { setPersonalization(null); return; }
    apiGet("/api/viqai/personalization")
      .then(j => { if (j?.success) setPersonalization(j.profile || { completed:false, answers:{} }); })
      .catch(() => setPersonalization({ completed:true, answers:{}, unavailable:true }));
  }, [user?.uid]);

  // v229 — Le compteur d'opportunités est un signal stable, pas une donnée
  // recalculée toutes les 30 s. Il se rafraîchit à l'ouverture puis uniquement
  // lorsqu'un scan est terminé ou que les objectifs sont modifiés.
  useEffect(() => {
    if (!user?.uid) return undefined;
    const refreshOpportunities = () => apiGet("/api/opportunities/summary")
      .then(j => { if (j?.success) setOpportunityCount(Number(j.count || 0)); })
      .catch(() => {});
    refreshOpportunities();
    window.addEventListener("viq-opportunities-changed", refreshOpportunities);
    window.addEventListener("viq-goals-updated", refreshOpportunities);
    return () => {
      window.removeEventListener("viq-opportunities-changed", refreshOpportunities);
      window.removeEventListener("viq-goals-updated", refreshOpportunities);
    };
  }, [user?.uid]);

  // v216 — Tutoriel interactif au premier accès à l'espace user. La clé est
  // scoped par UID pour qu'un navigateur partagé ne masque pas le guide au
  // prochain utilisateur. Le guide reste toujours relançable depuis le menu.
  useEffect(() => {
    if (!user?.uid) return;
    const openTourWhenReady = () => {
      try {
        const goalsKey = `viq_goals_intro_v224_${user.uid}`;
        const tourKey = `viq_user_tour_v240_${user.uid}`;
        if (localStorage.getItem(goalsKey) === "1" && !localStorage.getItem(tourKey)) { setTourStep(0); setTourOpen(true); }
      } catch (e) {}
    };
    openTourWhenReady();
    window.addEventListener("viq-goals-intro-complete", openTourWhenReady);
    return () => window.removeEventListener("viq-goals-intro-complete", openTourWhenReady);
  }, [user?.uid]);

  // Auto-scroll conditionnel : on ne ramène en bas que si l'utilisateur y
  // était déjà. Sinon, relire une réponse précédente devient impossible.
  const onThreadScroll = () => {
    const el = threadRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };
  useEffect(() => {
    const el = threadRef.current;
    if (el && stickToBottom.current) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [turns, steps]);

  // §6 — Handler UNIQUE : bouton +, glisser-déposer, collage et sélection de
  // fichier finissent tous ici. Avoir plusieurs chemins produisait des états
  // différents selon la manière d'importer — c'est ce qui rendait le
  // glisser-déposer instable.
  const handleIncomingFiles = useCallback(async (files, importSource = "auto") => {
    const next = [];
    for (const f of files.slice(0, MAX_ATTACHMENTS - attachments.length)) {
      if (f.type === "application/pdf") {
        const data = await new Promise(res => {
          const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => res(null); r.readAsDataURL(f);
        });
        if (data) next.push({ kind: "pdf", data, name: f.name, import_source: importSource, semantic_type: classifyAttachment(f) });
      } else if (f.type.startsWith("image/")) {
        const data = await compressImageFile(f);
        if (data) next.push({ kind: "image", data, name: f.name, import_source: importSource, semantic_type: classifyAttachment(f) });
      }
    }
    const startIndex = attachments.length;
    const provisional = next.map(x => ({...x, detecting:true}));
    setAttachments(a => [...a, ...provisional].slice(0, MAX_ATTACHMENTS));
    provisional.forEach(async (att, offset) => {
      const r = await apiPost("/api/viqai/document-detect", { name:att.name, kind:att.kind, data:att.data, semantic_type:att.semantic_type }).catch(() => null);
      setAttachments(cur => cur.map((x, idx) => idx === startIndex + offset ? ({...x, detecting:false, detection:r?.detection || null, suggested_dossiers:r?.suggested_dossiers || []}) : x));
    });
    return next.length;
  }, [attachments.length]);

  const attachDetectedDocument = useCallback(async (index, dossier) => {
    const att = attachments[index];
    if (!att || !dossier?.id) return;
    const r = await apiPost(`/api/dossiers/${dossier.id}/files`, {
      type: att.detection?.type || att.semantic_type || "document",
      payload: { name:att.name, data:att.data, source:"viqai_document_detection", detection:att.detection || null }
    }).catch(() => null);
    if (r?.success) setAttachments(cur => cur.map((x,i)=>i===index?({...x,attached:true,suggested_dossiers:[]}):x));
  }, [attachments]);

  // ─── §10-11, §14 (v198) — Sélecteur de fichiers réellement ouvert ───────
  // « 📷 Ajouter des photos » envoyait une phrase au modèle, qui répondait en
  // expliquant où cliquer. Le bouton ouvre maintenant le sélecteur du système,
  // et l'envoi part avec les fichiers — sans aucun appel de modèle pour cette
  // étape.
  const actionFileRef = useRef(null);
  const actionKind = useRef(null);

  const openFilePicker = useCallback(({ accept = "image/*", multiple = true, dossierId = null, kind = "photos" } = {}) => {
    actionKind.current = { kind, dossierId };
    const el = actionFileRef.current;
    if (!el) return;
    el.accept = accept;
    el.multiple = multiple;
    el.value = "";
    el.click();
  }, []);

  // §14-15 — `mediaIds` accompagne le message : les boutons d'une carte média
  // désignent des identifiants, ils ne demandent plus au modèle de deviner ce
  // que « ces photos » veut dire.
  const send = useCallback(async (overrideText, confirmations, forcedProvider, mediaIds, skipPersonalization = false) => {
    const text = overrideText !== undefined ? overrideText : input;
    if (busy) return;
    if (!text.trim() && !attachments.length && !confirmations) return;
    if (boot?.access?.allowed === false || boot?.trial?.status === "expired") {
      setTurns(t => [...t, {
        role: "assistant",
        content: "Votre essai gratuit de 7 jours est terminé. Souscrivez à un forfait VIQ.AI pour continuer à utiliser l’intelligence artificielle.",
        cards: [],
      }]);
      navigate("billing");
      return;
    }

    // Premier échange : avant d'envoyer la demande au moteur, VIQ.AI recueille
    // son contexte de dirigeant. La demande initiale est conservée et repart
    // automatiquement dès la 15e réponse.
    if (!skipPersonalization && !confirmations) {
      let profileState = personalization;
      if (!profileState) {
        try {
          const pj = await apiGet("/api/viqai/personalization");
          profileState = pj?.profile || { completed:false, answers:{} };
          setPersonalization(profileState);
        } catch (_) { profileState = { completed:true, answers:{}, unavailable:true }; }
      }
      if (!profileState?.completed) {
        pendingPersonalizationSend.current = { overrideText, confirmations, forcedProvider, mediaIds };
        setPersonalizationOpen(true);
        return;
      }
    }

    setBusy(true);
    // v219 — Les rafraîchissements périodiques du cockpit/workflow sont
    // suspendus pendant un tour IA. Ils n'apportent rien à la réponse en cours
    // et pouvaient provoquer des rerenders/réseaux concurrents donnant
    // l'impression que le chargement VIQ.AI se figeait.
    if (typeof window !== "undefined") {
      window.__viqAiBusy = true;
      window.dispatchEvent(new CustomEvent("viq-ai-busy", { detail: { busy: true } }));
    }
    setSteps([]);
    setServerNext([]);
    stickToBottom.current = true;
    if (overrideText === undefined) setInput("");
    const sent = attachments;
    setAttachments([]);
    const startFresh = shouldStartFreshConversation(turns, text, sent);
    const requestConversationId = startFresh ? null : conversationId;
    if (startFresh) { setConversationId(null); setActiveContext(null); setTurns([]); }
    const attachmentMeta = sent.map(a => ({
      name:a.name || "Pièce jointe", kind:a.kind || null, semantic_type:a.semantic_type || null,
      detection:a.detection || null, suggested_dossiers:Array.isArray(a.suggested_dossiers) ? a.suggested_dossiers.slice(0,5) : []
    }));

    setTurns(t => [...t,
      { role: "user", content: text, attachments_count: sent.length, attachment_meta: attachmentMeta,
        thumbs: sent.filter(a => a.kind === "image").map(a => a.data) },
      { role: "assistant", content: "", cards: [] },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamMessage({
        message: text, conversation_id: requestConversationId, dossier_id: dossierId,
        // §32 — Une action « Réessayer Gemini » impose son moteur pour CE tour,
        // sans changer la préférence de l'utilisateur.
        provider: forcedProvider || provider, mode: interactionMode, attachments: sent, confirmations: confirmations || {}, city, weather,
        media_ids: Array.isArray(mediaIds) ? mediaIds : undefined,
      }, (ev) => {
        if (ev.type === "conversation") {
          setConversationId(ev.id);
          // Insertion sans doublon : une conversation déjà listée (reprise
          // après rechargement) revenait une seconde fois en tête.
          setConversations(c => [
            { id: ev.id, title: ev.title || "Nouvelle conversation", group: "Aujourd'hui", updated_at: new Date().toISOString() },
            ...c.filter(x => x.id !== ev.id),
          ]);
        }
        if (ev.type === "tool_start") {
          setSteps(s => [...s.map(x => x.state === "running" ? { ...x, state: "done" } : x),
                         { tool: ev.tool, label: ev.label, state: "running" }]);
        }
        if (ev.type === "tool_error") {
          setSteps(s => s.map(x => x.tool === ev.tool ? { ...x, state: "failed", label: ev.error } : x));
        }
        if (ev.type === "tool_result" || ev.type === "confirmation") {
          setSteps(s => s.map(x => x.tool === ev.tool ? { ...x, state: "done" } : x));
          const card = ev.type === "confirmation"
            ? { type: "action_confirmation", tool: ev.tool, tool_call_id: ev.tool_call_id, data: ev.data }
            : { type: ev.card, tool: ev.tool, data: ev.data };
          setTurns(t => {
            const c = [...t]; const last = c[c.length - 1];
            const existing = last.cards || [];
            const sig = viqCardSignature(card);
            if (existing.some(x => viqCardSignature(x) === sig)) return t;
            c[c.length - 1] = { ...last, cards: [...existing, card] };
            return c;
          });
          // v231 — Un scan VIQ.AI persiste ses candidats côté serveur. Rafraîchir
          // immédiatement le compteur d'accueil et les vues Opportunités évite
          // d'attendre un rechargement de page pour voir les résultats détectés.
          if (ev.tool === "scan_opportunity_radar" || ev.card === "opportunity_radar_results") {
            window.dispatchEvent(new Event("viq-opportunities-changed"));
          }
        }
        if (ev.type === "next_actions") setServerNext(Array.isArray(ev.actions) ? ev.actions : []);
        // §7, §36 — Le serveur signale le véhicule résolu : le bandeau suit.
        if (ev.type === "step" && /Véhicule (retrouvé|actif)/.test(ev.label || "") && ev.detail) {
          setActiveContext(c => ({ ...(c || {}), label: ev.detail }));
        }
        if (ev.type === "step" && /^Dossier VIQ #(\d+)/.test(ev.label || "")) {
          const id = parseInt(ev.label.match(/#(\d+)/)[1]);
          setActiveContext(c => ({ label: c?.label || `Dossier #${id}`, dossier_id: id }));
        }
        // §8 — Ouverture d'onglet demandée par le serveur, sans passer par le modèle.
        if (ev.type === "ui_action" && ev.action === "open_dossier") {
          onOpenDossier && onOpenDossier(ev.dossier_id, ev.tab || "overview");
        }
        if (ev.type === "text" || ev.type === "final") {
          setTurns(t => {
            const c = [...t]; const last = c[c.length - 1];
            const finalCards = ev.type === "final" && Array.isArray(ev.cards) ? ev.cards : (last.cards || []);
            const hasWebResearch = finalCards.some(x => x?.type === "web_research_results");
            // v253 — une recherche web n'affiche qu'une seule réponse : la
            // synthèse est intégrée à la carte de recherche et n'est plus
            // répétée une seconde fois comme paragraphe sous la carte.
            if (hasWebResearch && ev.text) {
              const patched = finalCards.map((x, i, arr) => {
                if (x?.type !== "web_research_results") return x;
                const isLast = !arr.slice(i + 1).some(y => y?.type === "web_research_results");
                return isLast ? { ...x, data: { ...(x.data || {}), synthesis: ev.text } } : x;
              });
              c[c.length - 1] = { ...last, cards: patched, content: "" };
              return c;
            }
            c[c.length - 1] = { ...last, cards: finalCards, content: ev.text || last.content };
            return c;
          });
        }
        if (ev.type === "error") {
          setTurns(t => {
            const c = [...t];
            c[c.length - 1] = { ...c[c.length - 1], content: ev.message || "La demande n'a pas abouti." };
            return c;
          });
        }
      }, controller.signal);
    } catch (e) {
      setTurns(t => {
        const c = [...t];
        c[c.length - 1] = { ...c[c.length - 1], content: e.message || "La demande n'a pas abouti." };
        return c;
      });
      if (e?.code === "SUBSCRIPTION_REQUIRED" || e?.subscribeRequired) navigate("billing");
    } finally {
      setBusy(false);
      if (typeof window !== "undefined") {
        window.__viqAiBusy = false;
        window.dispatchEvent(new CustomEvent("viq-ai-busy", { detail: { busy: false } }));
      }
      setSteps(s => s.map(x => x.state === "running" ? { ...x, state: "done" } : x));
      // La conversation active remonte en tête et change de groupe : le tri de
      // la sidebar se fait sur `updated_at`, qui vient de changer côté serveur.
      // Sans cette remontée, l'ordre affiché restait celui du chargement de la
      // page et une conversation reprise ce matin passait sous celles d'hier.
      setConversations(c => {
        const id = conversationId;
        if (!id) return c;
        const hit = c.find(x => x.id === id);
        if (!hit) return c;
        return [{ ...hit, group: "Aujourd'hui", updated_at: new Date().toISOString() },
                ...c.filter(x => x.id !== id)];
      });
    }
  }, [input, attachments, busy, conversationId, dossierId, provider, interactionMode, city, weather, boot, navigate, turns, personalization]);

  // v199 — CORRECTIF TDZ : ce gestionnaire doit être déclaré APRÈS `send`.
  // Son tableau de dépendances est évalué au rendu ; le placer avant `send`
  // provoquait « can't access lexical declaration ... before initialization ».
  const onActionFiles = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const { kind, dossierId } = actionKind.current || {};
    actionKind.current = null;
    const n = await handleIncomingFiles(files);
    if (!n) return;
    // §10 — Les photos ne sont JAMAIS perdues : si aucun dossier n'existe
    // encore, elles restent attachées à la conversation et seront transférées
    // au dossier au moment de sa création (create_dossier les récupère).
    const prompt = kind === "histovec"
      ? "Voici le rapport HistoVec de ce véhicule : importe-le dans le dossier."
      : dossierId
        ? "Ajoute ces photos au dossier."
        : "Voici les photos du véhicule.";
    send(prompt);
  }, [handleIncomingFiles, send]);

  // v188 — CORRECTIF : ce gestionnaire était placé AVANT `const send`, avec
  // `[send]` en tableau de dépendances. Ce tableau est évalué à chaque rendu :
  // lire `send` avant sa déclaration levait une ReferenceError de zone morte
  // temporelle, et l'espace user devenait inaccessible (le site public, qui ne
  // monte pas ce composant, continuait de fonctionner).
  //
  // La référence passe par un ref : l'écouteur est posé une seule fois et lit
  // toujours la dernière version de `send`, sans le mettre en dépendance.
  const sendRef = useRef(send);
  useEffect(() => { sendRef.current = send; }, [send]);
  useEffect(() => () => {
    if (typeof window !== "undefined") {
      window.__viqAiBusy = false;
      window.dispatchEvent(new CustomEvent("viq-ai-busy", { detail: { busy: false } }));
    }
  }, []);
  useEffect(() => {
    const ask = (e) => { const q = e.detail?.prompt; if (q) sendRef.current?.(q); };
    window.addEventListener("viq-ask", ask);
    return () => window.removeEventListener("viq-ask", ask);
  }, []);

  const confirmAction = (card) => {
    setResolvedConfirms(r => ({ ...r, [card.tool_call_id]: true }));
    // v219 — La confirmation transporte l'outil ET ses arguments exacts. Le
    // backend rejoue cette action de façon déterministe au lieu de demander au
    // LLM de réinterpréter « Confirmé : … » (ce qui pouvait transformer un
    // envoi d'e-mail en note CRM).
    send(`Confirmer : ${card.data.label}`, {
      [card.tool_call_id]: true,
      [card.tool]: true,
      __action: { tool: card.tool, tool_call_id: card.tool_call_id, args: card.data.args || {}, label: card.data.label || card.tool },
    });
  };
  const cancelAction = (card) => {
    setResolvedConfirms(r => ({ ...r, [card.tool_call_id]: "cancelled" }));
    setTurns(t => [...t, { role: "assistant", content: "Action annulée. Rien n'a été enregistré.", cards: [] }]);
  };

  const openConversation = async (id) => {
    setDrawerOpen(false);
    const j = await apiGet(`/api/viqai/conversations/${id}`).catch(() => null);
    if (!j?.success) return;
    setConversationId(id);
    setProvider(j.conversation.provider || "auto");
    setTurns(j.messages.map(m => {
      const cards = m.cards || [];
      const webSynthesisInCard = cards.some(c => c?.type === "web_research_results" && c?.data?.synthesis);
      return {
        role: m.role, content: webSynthesisInCard ? "" : m.content, cards,
        attachments_count: m.attachments_count, attachment_meta: m.attachment_meta || [],
      };
    }));
    stickToBottom.current = true;
  };

  const newConversation = () => {
    setConversationId(null); setTurns([]); setSteps([]);
    setInput(""); setAttachments([]); setDrawerOpen(false);
  };

  // §31-34, §71-74 — Exécution d'une action suggérée. Deux natures distinctes :
  //   • navigate / tel / open  → exécutées DIRECTEMENT côté interface. Passer
  //     « ouvrir le dossier 42 » par le modèle coûterait un aller-retour, une
  //     latence et un appel facturé pour une action déjà déterminée.
  //   • prompt / tool          → envoyées à VIQ.AI.
  // Dans tous les cas le composer est vidé : laisser la demande affichée après
  // l'avoir envoyée donne l'impression qu'elle n'est pas partie.
  const runSuggestedAction = (a) => {
    if (!a) return;
    // §13 — Le texte n'est JAMAIS remis dans le composer : une suggestion
    // cliquée est exécutée, pas préparée. Le champ est vidé d'abord pour que
    // l'utilisateur ne voie jamais sa demande « rester » après l'avoir lancée.
    setInput("");
    if (a.type === "new_conversation") {
      newConversation();
      if (a.prompt) setInput(String(a.prompt));
      return;
    }
    if (a.type === "navigate" && a.destination) return onNavigate && onNavigate(a.destination);
    // §8, §75-76 — Ouverture ciblée : dossier + onglet, lead précis, note
    // précise. Le frontend exécute sans repasser par le modèle (§96).
    if (a.type === "open_dossier" && (a.dossier_id || a.id)) {
      return onOpenDossier && onOpenDossier(a.dossier_id || a.id, a.tab || "overview");
    }
    if (a.type === "open_lead" && a.id) {
      window.dispatchEvent(new CustomEvent("viq-focus-lead", { detail:{ id:Number(a.id) } }));
      return onNavigate && onNavigate("leads", { id: a.id });
    }
    if (a.type === "open_sourcing_lead" && a.id) {
      apiPost(`/api/pro-leads/${Number(a.id)}/sourcing-profile`, {}).then(() => {
        window.dispatchEvent(new CustomEvent("viq-open-opportunity-scan", { detail:{ lead_id:Number(a.id) } }));
        window.dispatchEvent(new Event("viq-opportunities-changed"));
      }).catch(() => window.dispatchEvent(new CustomEvent("viq-open-opportunity-scan", { detail:{ lead_id:Number(a.id) } })));
      return;
    }
    if (a.type === "open_note" && a.id) return onNavigate && onNavigate("notes", { id: a.id });
    if (a.type === "open_workflow") return onNavigate && onNavigate("notes", { tab: "workflow" });
    // ─── §3-5, §14-15 — Actions média déterministes ────────────────────────
    // Ces actions transportent des `media_ids`. Elles ne repassent PAS par une
    // phrase que le modèle devrait réinterpréter : c'est ce qui faisait perdre
    // « ces photos » d'un tour à l'autre.
    if (a.type === "download_media" && a.media_ids?.length) {
      downloadMedia(a.media_ids[0]).catch(() => {});
      return;
    }
    if (a.type === "download_media_zip" && a.media_ids?.length) {
      downloadMediaZip(a.media_ids, { vehicle: a.vehicle || {}, mode: a.mode || null }).catch(() => {});
      return;
    }
    // v224 — ancienne action de partage neutralisée : elle pouvait exposer une URL backend.
    if (a.type === "share_media") return;
    if (a.type === "save_media_to_dossier" && a.dossier_id) {
      return onOpenDossier && onOpenDossier(a.dossier_id, "photos");
    }
    if (a.type === "generate_video") {
      return send(
        a.format ? `Crée une vidéo de présentation au format ${a.format}.`
          : a.with_branding === false ? "Crée la vidéo sans branding."
          : "Crée une vidéo de présentation avec ces photos.",
        undefined, undefined, a.media_ids || []
      );
    }
    if (a.type === "generate_listing") {
      return send("Génère l'annonce de vente avec ces visuels.", undefined, undefined, a.media_ids || []);
    }
    if (a.type === "retry_media") {
      return send(`Refais le traitement des photos en mode ${a.mode || "showroom"}.`, undefined, undefined, a.media_ids || []);
    }
    // §3 cahier 2 — CarVertical : le clic ouvre le lien affilié prérempli, sans
    // tour de modèle. L'URL est construite côté serveur, jamais devinée ici.
    if (a.type === "open_carvertical" && a.url) {
      window.open(a.url, "_blank", "noopener,noreferrer sponsored");
      return;
    }
    if (a.type === "open_media_job") return;   // la carte sonde déjà le job

    // ─── §10-14, §17 (v198) — VRAIES ACTIONS, PAS DES PROMPTS DÉGUISÉS ─────
    // « Ajouter des photos » envoyait le texte « ajouter photos » au modèle,
    // qui répondait en expliquant comment faire. Une action d'interface doit
    // appeler une fonction d'interface : ouvrir le sélecteur de fichiers coûte
    // zéro jeton et zéro seconde d'attente.
    if (a.type === "add_photos" || a.type === "open_file_picker") {
      openFilePicker({ accept: "image/*", multiple: true, dossierId: a.dossier_id || null, kind: "photos" });
      return;
    }
    if (a.type === "import_histovec") {
      // HistoVec accepte le PDF officiel comme une capture d'écran.
      openFilePicker({ accept: "application/pdf,image/*", multiple: false, dossierId: a.dossier_id || null, kind: "histovec" });
      return;
    }
    // §12 — CarVertical : le VIN est connu, l'URL se construit sans modèle.
    // Ouvrir un lien n'a jamais demandé de raisonnement.
    if (a.type === "open_carvertical" && a.url) {
      window.open(a.url, "_blank", "noopener,noreferrer");
      return;
    }
    // §8, §17 — Naviguer vers un onglet est une action d'interface.
    if (a.type === "open_dossier" && a.dossier_id) {
      onOpenDossier && onOpenDossier(a.dossier_id, a.tab || "overview");
      return;
    }
    if (a.type === "open_workflow") {
      window.dispatchEvent(new CustomEvent("viq-open-workflow"));
      return;
    }
    if (a.type === "link" && a.url) {
      window.open(a.url, "_blank", "noopener,noreferrer");
      return;
    }
    // §6 — Publier au réseau : la modale de publication existante s'ouvre par
    // évènement global, comme le reste des vues réseau.
    if (a.type === "publish_dossier") {
      window.dispatchEvent(new CustomEvent("viq-publish-dossier", { detail: { dossier_id: a.dossier_id || null } }));
      return;
    }
    // §12 — Une action outillée porte son outil ET son contexte. Elle
    // s'exécute, elle ne se contente pas d'écrire une phrase dans le composer.
    if (a.type === "tool" && a.tool && a.payload) {
      return send(a.prompt || a.label, undefined, a.provider);
    }
    if (a.type === "tool" && a.prompt) return send(a.prompt, undefined, a.provider);
    if (a.type === "network" && a.id) { window.dispatchEvent(new CustomEvent("viq-open-network", { detail: { id: a.id } })); return; }
    if (a.type === "tel" && a.phone) { window.location.href = `tel:${String(a.phone).replace(/[\s.]/g, "")}`; return; }
    if (a.type === "link" && a.url) { window.open(a.url, "_blank", "noopener,noreferrer"); return; }
    const text = a.prompt || a.ask || a.label;
    if (!text) return;
    if (a.provider) { send(text, undefined, a.provider); return; }
    // Une formulation qui se termine par « : » ou un espace attend un
    // complément de l'utilisateur : on la dépose dans le composer au lieu de
    // l'envoyer incomplète.
    if (/[\s:]$/.test(text)) { setInput(text); return; }
    send(text);
  };

  const empty = turns.length === 0;
  // v214 — La formule de politesse suit l'heure LOCALE du navigateur, pas
  // l'heure du serveur. Elle est recalculée au montage, au retour sur l'onglet
  // et chaque minute : une session laissée ouverte ne garde plus "Bonjour"
  // toute l'après-midi.
  const [localNow, setLocalNow] = useState(() => new Date());
  useEffect(() => {
    const refreshClock = () => setLocalNow(new Date());
    const timer = window.setInterval(refreshClock, 60 * 1000);
    window.addEventListener("focus", refreshClock);
    document.addEventListener("visibilitychange", refreshClock);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshClock);
      document.removeEventListener("visibilitychange", refreshClock);
    };
  }, []);
  const hour = localNow.getHours();
  const greeting = hour < 6 ? "Bonne nuit" : hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir";

  const composer = (
    <Composer value={input} onChange={setInput} onSend={() => send()} busy={busy}
      attachments={attachments} onAttach={handleIncomingFiles}
      onRemoveAttachment={i => setAttachments(a => a.filter((_, k) => k !== i))}
      onAttachToDossier={attachDetectedDocument}
      providers={boot?.providers || [{ id: "auto", label: "Auto", available: true }]}
      provider={provider} onProvider={setProvider}
      interactionMode={interactionMode} onInteractionMode={setInteractionMode} autoFocus={empty} />
  );

  return (
    <div className={`viq-chat${drawerOpen ? " drawer-open" : ""}`}
      onDragEnter={e => {
        e.preventDefault();
        if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
        dragDepth.current += 1;
        setDragging(true);
      }}
      onDragOver={e => e.preventDefault()}
      onDragLeave={e => {
        e.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragging(false);
      }}
      onDrop={e => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        // Le texte déjà saisi n'est jamais touché : déposer une photo ne doit
        // pas effacer une question en cours de rédaction.
        handleIncomingFiles(Array.from(e.dataTransfer?.files || []));
      }}
      onPaste={e => {
        const files = Array.from(e.clipboardData?.files || []);
        if (files.length) { e.preventDefault(); handleIncomingFiles(files); }
      }}>

      {dragging && (
        <div className="viq-dropzone">
          <strong>Déposez vos photos ici</strong>
          <span>VIQ.AI les analysera automatiquement</span>
        </div>
      )}

      {/* §10-11, §14 — Sélecteur de fichiers des actions rapides. Invisible,
          déclenché par « Ajouter des photos » et « Importer HistoVec ». */}
      <input ref={actionFileRef} type="file" hidden onChange={onActionFiles}
        aria-hidden="true" tabIndex={-1} />

      <AgentPersonalizationWizard
        open={personalizationOpen}
        required
        initialAnswers={personalization?.answers}
        onComplete={(profile) => {
          setPersonalization(profile);
          setPersonalizationOpen(false);
          const pending = pendingPersonalizationSend.current;
          pendingPersonalizationSend.current = null;
          if (pending) setTimeout(() => send(pending.overrideText, pending.confirmations, pending.forcedProvider, pending.mediaIds, true), 0);
        }}
      />

      {/* §47 — Après suppression de la navbar, le haut se réduit à l'essentiel :
          le hamburger et l'accès à une nouvelle conversation. Tout le reste de
          la hauteur revient à la conversation. */}
      <header className="viq-topbar">
        <button type="button" className="viq-tool-btn viq-menu-btn" aria-label="Ouvrir la navigation"
          aria-expanded={drawerOpen}
          onClick={() => {
            setDrawerOpen(true);
            // Resynchronisation à l'ouverture : les titres sont dérivés côté
            // serveur du premier message, et une conversation ouverte dans un
            // autre onglet n'apparaîtrait jamais autrement.
            apiGet("/api/viqai/conversations")
              .then(j => { if (j?.success) setConversations(j.conversations); })
              .catch(() => {});
          }}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <button type="button" className="viq-topbar-new" onClick={newConversation}
          title="Démarrer une nouvelle conversation">
          {conversationId
            ? (conversations.find(c => c.id === conversationId)?.title || "Conversation")
            : "Nouvelle conversation"}
        </button>
        {/* §5 — Le bouton notes n'existe QUE sur petit écran. Sur desktop le
            bloc-notes est permanent : un bouton pour l'ouvrir n'y a aucun sens,
            et une croix pour le fermer non plus. */}
        {showNotesToggle && (
          <button type="button" className={`viq-tool-btn viq-notes-toggle${notesToggleLabel ? " labeled" : ""}`} aria-label="Afficher le Workflow Orchestrator"
            onClick={onToggleNotes}>
            <span aria-hidden="true">☷</span>{notesToggleLabel && <b>{notesToggleLabel}</b>}
          </button>
        )}
      </header>

      <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}
        conversations={conversations} activeId={conversationId}
        onNew={newConversation} onOpen={openConversation}
        onRename={async (id, title) => {
          await apiPatch(`/api/viqai/conversations/${id}`, { title }).catch(() => {});
          setConversations(c => c.map(x => x.id === id ? { ...x, title } : x));
        }}
        onDelete={async (id) => {
          await apiDelete(`/api/viqai/conversations/${id}`).catch(() => {});
          setConversations(c => c.filter(x => x.id !== id));
          if (id === conversationId) newConversation();
        }}
        onNavigate={navigate} userName={firstName}
        isAdmin={isAdmin} darkMode={darkMode}
        onToggleTheme={onToggleTheme} onLogout={onLogout}
        onOpenOpportunityScan={() => setOpportunityModalOpen(true)}
        onStartTour={() => { setTourStep(0); setTourOpen(true); }} />

      <OpportunityScanModal open={opportunityModalOpen}
        focusLeadId={opportunityFocusLeadId}
        onClose={() => { setOpportunityModalOpen(false); setOpportunityFocusLeadId(null); }}
        onChanged={() => window.dispatchEvent(new Event("viq-opportunities-changed"))}
        onShowResults={() => navigate("opportunities")} />

      {tourOpen && (() => {
        const steps = [
          { icon:"◈", title:"Choisir le moteur IA", selector:".viq-model-btn", text:"Le sélecteur de moteur choisit automatiquement le modèle adapté à la tâche. Vous pouvez aussi sélectionner explicitement un moteur disponible, dont Mistral lorsqu'il est configuré, pour une analyse ou un raisonnement ciblé." },
          { icon:"✦", title:"Interroger VIQ.AI", selector:".viq-composer", text:"La zone de saisie pilote l'ensemble de l'espace. Auto reste dans le contexte métier VehicleIQ et exploite dossiers, CRM, workflow et intégrations ; Web lance une recherche web pure, hors contexte métier. Le bouton + ajoute photos et documents." },
          { icon:"⚡", title:"Actions rapides", selector:".viq-welcome-quick-actions", text:"Sur ordinateur, quatre raccourcis contextuels apparaissent sous la zone de saisie pour lancer immédiatement les actions du moment : emails prioritaires, agenda, relances et autres signaux détectés par VIQ.AI." },
          { icon:"↺", title:"Historique et conversations", selector:".viq-topbar-new", text:"Chaque conversation conserve son contexte. Nouvelle conversation démarre un sujet propre ; lorsque votre demande change totalement de domaine, VIQ.AI peut aussi isoler automatiquement le nouveau sujet pour éviter de mélanger les contextes." },
          { icon:"☰", title:"Navigation principale", selector:".viq-menu-btn", text:"Le bouton menu ouvre la navigation complète : conversations, équipe, Dashboard Pro, documents, prospection, comptabilité, abonnement et paramètres. Le rail gauche reste volontairement limité aux raccourcis utilisés toute la journée." },
          { icon:"🚘", title:"Raccourcis métier essentiels", selector:".viq-command-rail", text:"Sur ordinateur, le rail gauche donne un accès direct à VIQ.AI, Dossiers, Leads / CRM, Sourcing, Agenda et Notes & relances. Les bulles indiquent les éléments réellement à traiter, dont les sourcings actifs et les notes ouvertes." },
          { icon:"👤", title:"Mon espace, paiements et notifications", selector:".viq-menu-btn", text:"Mon espace gère votre profil et vos informations. Paiements & abonnement centralise offre et facturation. Notifications permet de régler les canaux et événements utiles afin que VIQ.AI vous informe au bon moment." },
          { icon:"🤝", title:"Partenaires et administration", selector:".viq-menu-btn", text:"Programme partenaires donne accès au parcours apporteur. Les comptes autorisés voient aussi Administration pour les utilisateurs, offres, paiements, datasets et analyses consolidées. Les droits restent appliqués côté serveur." },
          { icon:"🎯", title:"Objectifs VIQ.AI", selector:".viq-goals-link", text:"Définissez objectifs de marge, ventes et critères véhicule. Ces objectifs alimentent les priorités, relances, profils de sourcing et suggestions proactives de VIQ.AI." },
          { icon:"⌕", title:"VIQ.AI Sourcing", selector:".viq-opportunity-count", text:"Configurez jusqu'à 10 profils actifs. Les demandes d'achat identiques sont regroupées et les demandes supplémentaires passent en file d'attente. Chaque profil indique sa dernière date de sourcing et les sources déjà qualifiées." },
          { icon:"✓", title:"Workflow Orchestrator", selector:".viq-notes", text:"Le Workflow Orchestrator agrège les signaux de la plateforme, propose des priorités et permet de créer, assigner et suivre les actions sans changer d'outil." },
          { icon:"▤", title:"Onglets du Workflow Orchestrator", selector:".viq-notes-tabs", text:"Les onglets se parcourent horizontalement : Votre résumé, Workflow, Leads, Notes, Appels, RDV et Intégration. Faites défiler la barre pour accéder à l'ensemble sans réduire la largeur du contenu." },
          { icon:"▥", title:"KPI et tableaux de bord", selector:".viq-performance-carousel", text:"Les KPI synthétisent marge, ventes, stock, workflow, leads, sourcing et signaux provenant des intégrations. Ils permettent de passer d'un indicateur à une action ou un objectif pilotable." },
          { icon:"⇄", title:"Intégrations", selector:".viq-notes-tabs", text:"Gmail/Outlook, calendriers, WhatsApp, YouTube Analytics, CarVertical et webhooks remontent leurs signaux dans un espace commun. VIQ.AI peut lire le contexte autorisé et déclencher les actions exposées par chaque intégration." },
          { icon:"📄", title:"Documents et pièces jointes", selector:".viq-composer", text:"La détection faite à l'import est conservée dans la conversation : carte grise, HistoVec, facture, CERFA, assurance et autres documents restent identifiés séparément. Plaque/VIN servent à proposer un rattachement au Dossier VIQ existant et des actions adaptées." },
          { icon:"🏷", title:"Leads par catégorie", selector:".viq-notes", text:"Les leads sont distingués par besoin : Recherche véhicule, Estimation ou Carte grise. Une demande d'achat alimente un profil sourcing plutôt qu'un dossier véhicule ; Afficher ouvre directement le lead concerné." },
          { icon:"⚙", title:"VIQ.AI coordonne les outils", selector:".viq-notes", text:"Selon vos droits, VIQ.AI lit, crée et met à jour les éléments accessibles : dossiers, leads, sourcing, tâches, rendez-vous, documents et intégrations. Les actions importantes restent structurées et proposées sous forme de boutons explicites." },
          { icon:"◎", title:"Piloter sans changer d'outil", selector:".viq-notes", text:"VIQ.AI relie les historiques, véhicules, demandes clients, sourcing et workflow. Les réponses évitent les répétitions et privilégient une synthèse structurée avec les actions suivantes réellement disponibles." },
        ];
        const st = steps[tourStep] || steps[0];
        const finish = () => { try { localStorage.setItem(`viq_user_tour_v240_${user?.uid || 'user'}`, '1'); } catch(e){} setTourOpen(false); };
        return <div className="viq-tour-overlay" role="dialog" aria-modal="true" aria-label="Visite guidée VehicleIQ">
          <div className="viq-tour-card">
            <div className="viq-tour-progress"><span style={{width:`${((tourStep+1)/steps.length)*100}%`}} /></div>
            <button className="viq-tour-close" onClick={finish} aria-label="Fermer">×</button>
            <div className="viq-tour-icon">{st.icon}</div>
            <div className="viq-tour-kicker">ÉTAPE {tourStep+1} / {steps.length}</div>
            <h2>{st.title}</h2>
            <p>{st.text}</p><div className="viq-tour-hint">Cliquez sur la zone animée pour continuer, ou utilisez Suivant.</div>
            <div className="viq-tour-actions">
              <button className="viq-tour-skip" onClick={finish}>Terminer plus tard</button>
              <div>
                {tourStep>0 && <button className="viq-tour-prev" onClick={()=>setTourStep(x=>Math.max(0,x-1))}>Précédent</button>}
                <button className="viq-tour-next" onClick={()=> tourStep===steps.length-1 ? finish() : setTourStep(x=>x+1)}>{tourStep===steps.length-1 ? "Commencer" : "Suivant"}</button>
              </div>
            </div>
          </div>
        </div>;
      })()}

      {empty ? (
        <div className="viq-welcome">
          <div className="viq-welcome-inner">
            <h1>{greeting}{firstName ? <> <span className="viq-greeting-name">{firstName}</span></> : ""}</h1>
            <div className="viq-meta">
              {(weather || city) && <span className="viq-weather-under-greeting">{weather || city}</span>}
              {/* §44 (passe précédente) — l'expiration ne doit jamais ressembler
                  à une panne : on annonce ce qui reste, et à son terme on
                  oriente vers les forfaits, sans message d'erreur. */}
              {boot?.trial?.status === "active" && boot.trial.days_left != null && (
                <span className="viq-trial">
                  · Essai VIQ.AI — {boot.trial.hours_left != null && boot.trial.hours_left < 24 ? `${boot.trial.hours_left} h` : `${boot.trial.days_left} jour${boot.trial.days_left > 1 ? "s" : ""}`} restant{boot.trial.hours_left != null && boot.trial.hours_left < 24 ? "es" : (boot.trial.days_left > 1 ? "s" : "")}
                  <button type="button" className="viq-trial-subscribe" onClick={() => navigate("billing")}>Souscrire</button>
                </span>
              )}
              {boot?.trial?.status === "expired" && (
                <button type="button" className="viq-trial ended" onClick={() => navigate("billing")}>
                  · Essai terminé — découvrir les forfaits VIQ.AI
                </button>
              )}
            </div>
            <div className="viq-ask">Que souhaitez-vous faire ?</div>
            <div className="viq-welcome-links">
              <button type="button" className="viq-goals-link" onClick={onOpenGoals}>Fixer des objectifs</button>
              <span aria-hidden="true">|</span>
              <button type="button" className="viq-opportunity-count clickable" onClick={() => setOpportunityModalOpen(true)}>Sourcer des véhicules</button>
            </div>
            {composer}
            {!busy && <div className="viq-welcome-quick-actions" aria-label="Actions rapides">
              {(boot?.suggestions || []).slice(0, 4).map((s, i) => (
                <button key={s.id || i} type="button" onClick={() => runSuggestedAction(s)} title={s.label}>
                  {s.label}
                </button>
              ))}
            </div>}
          </div>
          <LegalFooter onLegal={onLegal} />
        </div>
      ) : (
        <>
          <div className="viq-thread" ref={threadRef} onScroll={onThreadScroll}>
            {turns.map((t, i) => (
              <div key={i} className={`viq-turn ${t.role}`}>
                {t.role === "user" ? (
                  <div className="viq-bubble-user">
                    {t.content}
                    {t.attachments_count > 0 && (
                      <>
                        <div className="viq-att-count">
                          {t.attachments_count} pièce{t.attachments_count > 1 ? "s" : ""} jointe{t.attachments_count > 1 ? "s" : ""}
                        </div>
                        {t.thumbs?.length > 0 && (
                          <div className="viq-gallery">
                            {t.thumbs.slice(0, 3).map((src, k) => <img key={k} src={src} alt="" />)}
                            {t.thumbs.length > 3 && <span className="more">+{t.thumbs.length - 3}</span>}
                          </div>
                        )}
                        {t.attachment_meta?.length > 0 && <div className="viq-attachment-context-list">
                          {t.attachment_meta.map((a,k)=>{
                            const det=a.detection||{}; const type=det.type||a.semantic_type||"other"; const dossier=a.suggested_dossiers?.[0];
                            const identifier=det.plate||det.vin||"";
                            const promptByType={
                              registration_certificate:`Analyse la carte grise ${a.name}${identifier?` (${identifier})`:""}, conserve son identité documentaire et propose les actions utiles sur le dossier existant.`,
                              histovec:`Analyse le rapport HistoVec ${a.name}, rattache-le au véhicule concerné et synthétise les points de vigilance.`,
                              invoice:`Analyse la facture ${a.name}, extrais les travaux et montants puis propose son rattachement au dossier concerné.`,
                              cerfa_cession:`Analyse le CERFA ${a.name}, vérifie les informations de cession et propose les actions dossier adaptées.`,
                              insurance:`Analyse le document d'assurance ${a.name} et rattache les informations utiles au bon dossier.`,
                              technical_inspection:`Analyse le contrôle technique ${a.name}, synthétise les défauts et propose les actions de remise en état.`,
                              listing:`Analyse cette annonce ${a.name}, identifie le véhicule et évalue l'opportunité d'achat.`,
                            };
                            return <div className="viq-attachment-context" key={`${a.name}-${k}`}><div><strong>{attachmentTypeLabel(type)}</strong><span>{a.name}{identifier?` · ${identifier}`:""}</span></div><div className="viq-attachment-actions">{dossier&&<button type="button" onClick={()=>onOpenDossier?.(dossier.id,"overview")}>Ouvrir le dossier</button>}<button type="button" onClick={()=>runSuggestedAction({prompt:promptByType[type]||`Analyse ${a.name} dans son contexte documentaire et propose les actions VehicleIQ pertinentes.`})}>Actions VIQ.AI</button></div></div>;
                          })}
                        </div>}
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    {i === turns.length - 1 && steps.length === 0 && <VIQThinking steps={steps} busy={busy} />}
                    {(t.cards || []).map((c, k) => (
                      <ViqCard key={k} card={c}
                        onAsk={(q) => runSuggestedAction({ prompt: q })}
                        onAction={runSuggestedAction}
                        onOpenDossier={onOpenDossier}
                        onOpenInterface={onOpenInterface}
                        onConfirm={confirmAction}
                        onCancel={cancelAction}
                        onUpgrade={() => navigate("billing")}
                        resolved={resolvedConfirms[c.tool_call_id]} />
                    ))}
                    {/* §34 — Les prochaines actions n'apparaissent que sous la
                        DERNIÈRE carte du dernier tour : les répéter sous chaque
                        carte d'un historique transformerait la conversation en
                        mur de boutons. */}
                    {/* §23 — Les suggestions viennent du serveur, qui connaît tout
                        le contexte (CarVertical déjà préparé, HistoVec lu, prix
                        d'achat connu…). La liste locale par carte ne sert plus
                        que de repli si le flux n'en a pas envoyé. */}
                    {i === turns.length - 1 && !busy && (serverNext.length > 0 ? (
                      <div className="viq-next">
                        {serverNext.map((a, k) => (
                          <button key={k} type="button"
                            className={`viq-suggestion${a.primary ? " primary" : ""}`}
                            onClick={() => runSuggestedAction(a)}>
                            {a.icon ? `${a.icon} ` : ""}{a.label}
                          </button>
                        ))}
                      </div>
                    ) : (t.cards || []).length > 0 && (
                      <NextActions card={t.cards[t.cards.length - 1]}
                        onAsk={(q) => runSuggestedAction({ prompt: q })} />
                    ))}
                    {cleanAssistantText(t.content) && !(t.cards || []).some(c => ["lead_list","sourcing_results","opportunity_radar_results"].includes(c.type)) && <ViqMarkdown className="viq-answer">{cleanAssistantText(t.content)}</ViqMarkdown>}
                    {i === turns.length - 1 && !busy && serverNext.length === 0 && assistantActionPrompts(t.content).length > 0 && (
                      <div className="viq-inline-actions" aria-label="Actions proposées par VIQ.AI">
                        {assistantActionPrompts(t.content).map((a, k) => <button key={k} type="button" onClick={() => runSuggestedAction(a)}>{a.label}<span aria-hidden="true">→</span></button>)}
                      </div>
                    )}
                    {/* Les étapes restent SOUS le contenu déjà rendu : le
                        travail en cours se lit à la suite de ce qui est acquis. */}
                    {i === turns.length - 1 && steps.length > 0 && <VIQThinking steps={steps} busy={busy} />}
                  </>
                )}
              </div>
            ))}
          </div>
          {/* §36 — Contexte actif. Le professionnel doit voir sur quel véhicule
          VIQ.AI travaille, et pouvoir en sortir d'un clic — sans quoi il ne
          sait jamais si « ce véhicule » désigne le bon. */}
      {activeContext && (
        <div className="viq-context-bar">
          <span className="viq-context-dot" aria-hidden="true">●</span>
          <button type="button" className="viq-context-label"
            onClick={() => activeContext.dossier_id && onOpenDossier && onOpenDossier(activeContext.dossier_id, "overview")}>
            {activeContext.label}
          </button>
          <button type="button" className="viq-context-close" aria-label="Quitter ce contexte"
            onClick={() => setActiveContext(null)}>✕</button>
        </div>
      )}

      <div className="viq-composer-wrap">
        {composer}
        {!busy && <div className="viq-welcome-quick-actions viq-thread-quick-actions" aria-label="Actions rapides">
          {(boot?.suggestions || []).slice(0, 4).map((s, i) => (
            <button key={s.id || i} type="button" onClick={() => runSuggestedAction(s)} title={s.label}>{s.label}</button>
          ))}
        </div>}
      </div>
          <LegalFooter onLegal={onLegal} />
        </>
      )}
    </div>
  );
}
